<script setup>
import SaveConfigurationValueAction from '@frontJs/components/actions/SaveConfigurationValueAction.vue';
import ArrowLineDownIcon from '@iconify-vue/ph/arrow-line-down';
import FileTextIcon from '@iconify-vue/ph/file-text';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import ShieldCheckIcon from '@iconify-vue/ph/shield-check-fill';
import TagIcon from '@iconify-vue/ph/tag';
import WarningIcon from '@iconify-vue/ph/warning-fill';
import DisplayContentAction from '@js/components/actions/DisplayContentAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CardGrid from '@js/components/cards/CardGrid.vue';
import MetricCard from '@js/components/cards/MetricCard.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import moment from 'moment';
import { h } from 'vue';

const {
    phpVersions,
    cms = null,
    openmage = null,
} = defineProps({
    phpVersions: {
        type: Array,
        required: true,
    },
    cms: {
        type: String,
        required: false,
    },
    openmage: {
        type: Boolean,
        required: false,
        default: null,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    globalData,
    updateData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.name,
                    href: route('front.projectManagement.dev.website', {
                        tenant: props.row.original.id,
                    }),
                    inertia: true,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'cms',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CMS/Framework',
                }),
            cell: (props) =>
                props.row.original.cms
                    ? h(PillCell, {
                          pillValue: props.row.original.cms.label,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'cmsVersion',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Version<br/>CMS/Framework',
                }),
            cell: (props) =>
                props.row.original.cmsVersion
                    ? h(TextCell, {
                          text: props.row.original.cmsVersion,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'cmsVersionMaintainedUntil',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: "Version CMS/Framework<br/>maintenue jusqu'à",
                }),
            cell: (props) =>
                props.row.original.cmsVersionMaintainedUntil
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.cmsVersionMaintainedUntil,
                          ),
                          color:
                              props.row.original.isCmsVersionMaintained ===
                              false
                                  ? 'red'
                                  : null,
                          tooltipContent: props.row.original
                              .lastCurrentCmsVersion
                              ? 'Dernière version du CMS courant disponible : <b>' +
                                props.row.original.lastCurrentCmsVersion +
                                '</b>'
                              : null,
                          tooltipPlacement: 'top',
                          tooltipIcon: WarningIcon,
                          tooltipColor:
                              props.row.original.isCmsVersionMaintained ===
                                  false &&
                              props.row.original
                                  .isUpToDateOnCurrentCmsVersion === false
                                  ? 'red'
                                  : props.row.original
                                          .isCmsVersionMaintained === false &&
                                      props.row.original
                                          .isUpToDateOnCurrentCmsVersion ===
                                          true
                                    ? 'orange'
                                    : props.row.original
                                            .isCmsVersionMaintained === true &&
                                        props.row.original
                                            .isUpToDateOnCurrentCmsVersion ===
                                            false
                                      ? 'yellow'
                                      : props.row.original
                                              .isCmsVersionMaintained ===
                                              true &&
                                          props.row.original
                                              .isUpToDateOnCurrentCmsVersion ===
                                              true
                                        ? 'green'
                                        : null,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'phpVersion',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Version PHP',
                }),
            cell: (props) =>
                props.row.original.phpVersion
                    ? h(TextCell, {
                          text: props.row.original.phpVersion,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'phpVersionMaintainedUntil',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: "Version PHP<br/>maintenue jusqu'à",
                }),
            cell: (props) =>
                props.row.original.phpVersion
                    ? h(TextCell, {
                          text: phpVersions.find(
                              (item) =>
                                  item.value === props.row.original.phpVersion,
                          ).maintainedUntil,
                          tooltipContent:
                              moment(
                                  phpVersions.find(
                                      (item) =>
                                          item.value ===
                                          props.row.original.phpVersion,
                                  ).maintainedUntil,
                                  'DD/MM/YYYY',
                              ) < moment()
                                  ? 'La version PHP <b>' +
                                    props.row.original.phpVersion +
                                    "</b> n'est plus maintenue."
                                  : null,
                          tooltipIcon: WarningIcon,
                          tooltipPlacement: 'top',
                          tooltipColor: 'red',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'twoFactorAuthenticationEnabled',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: '2FA admin',
                }),
            cell: (props) =>
                props.row.original.twoFactorAuthenticationEnabled
                    ? h(ShieldCheckIcon, {
                          class: 'h-[52px] text-green-500 p-4',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'cspEnabled',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CSP',
                }),
            cell: (props) =>
                props.row.original.cspEnabled
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () =>
                                  h(ShieldCheckIcon, {
                                      class: 'size-5 text-green-500',
                                  }),
                          },
                      )
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'cloudflareEnabled',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Cloudflare',
                }),
            cell: (props) =>
                props.row.original.cloudflareEnabled
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () =>
                                  h(ShieldCheckIcon, {
                                      class: 'size-5 text-green-500',
                                  }),
                          },
                      )
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'cves',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CVES',
                }),
            cell: (props) =>
                props.row.original.tenantCves.length > 0
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () =>
                                  h(ShieldCheckIcon, {
                                      class: props.row.original
                                          .allTenantCveIsApplied
                                          ? 'size-5 text-green-500'
                                          : 'size-5 text-red-500',
                                  }),
                          },
                      )
                    : null,
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'details',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Détails',
                    width: 0,
                }),
            cell: (props) =>
                props.row.original.securityNotes
                    ? h(
                          BaseCell,
                          {},
                          {
                              default: () =>
                                  h(
                                      DisplayContentAction,
                                      {
                                          title: 'Notes de sécurité',
                                          content:
                                              props.row.original.securityNotes,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Voir les notes de sécurité',
                                                  type: 'primary',
                                                  icon: FileTextIcon,
                                              }),
                                      },
                                  ),
                          },
                      )
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () =>
                            h(ActionButton, {
                                size: 'small',
                                square: true,
                                title: 'Modifier',
                                href: route(
                                    'front.projectManagement.dev.website',
                                    {
                                        tenant: props.row.original.id,
                                    },
                                ),
                                inertia: true,
                                icon: PencilSimpleIcon,
                            }),
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.dev.postWebsites'),
    data: {
        cms: cms,
        openmage: openmage,
    },
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        cms: cms === null,
        cmsVersion: cms !== 'shopify' && cms !== 'static',
        cmsVersionMaintainedUntil: cms !== 'shopify' && cms !== 'static',
        phpVersion: cms !== 'shopify' && cms !== 'nextjs',
        phpVersionMaintainedUntil: cms !== 'shopify' && cms !== 'nextjs',
        twoFactorAuthenticationEnabled:
            cms !== 'shopify' &&
            cms !== 'nextjs' &&
            cms !== 'static' &&
            cms !== 'wordpress' &&
            cms !== 'laravel' &&
            cms !== 'prestashop',
        cspEnabled:
            cms !== 'shopify' &&
            cms !== 'nextjs' &&
            cms !== 'static' &&
            cms !== 'wordpress' &&
            cms !== 'laravel' &&
            cms !== 'prestashop',
        cloudflareEnabled: cms !== 'shopify',
        cves:
            cms !== 'shopify' &&
            cms !== 'nextjs' &&
            cms !== 'static' &&
            cms !== 'wordpress',
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
    >
        <template #before-table>
            <CardGrid v-if="cms === 'magento1'" :columns="2">
                <MetricCard
                    title="Dernière version"
                    :icon="TagIcon"
                    :value="
                        globalData.latestMagento1Version.data.value ?? 'N/A'
                    "
                >
                    <template #actions>
                        <SaveConfigurationValueAction
                            configuration-key="latest_magento1_version"
                            title="Mettre à jour la dernière version de Magento 1"
                            label="Dernière version"
                            :callback="updateData"
                        >
                            <template #trigger>
                                <ActionButton
                                    size="small"
                                    type="secondary"
                                    square
                                    title="Modifier la dernière version de Magento 1"
                                    :icon="PencilSimpleIcon"
                                >
                                </ActionButton>
                            </template>
                        </SaveConfigurationValueAction>
                    </template>
                </MetricCard>

                <MetricCard
                    title="Dernière version minimale supportée"
                    :icon="ArrowLineDownIcon"
                    :value="
                        globalData.latestMagento1MinimumSupportedVersion.data
                            .value ?? 'N/A'
                    "
                >
                    <template #actions>
                        <SaveConfigurationValueAction
                            configuration-key="latest_magento1_minimum_supported_version"
                            title="Mettre à jour la dernière version minimale supportée de Magento 1"
                            label="Dernière version minimale supportée"
                            :callback="updateData"
                        >
                            <template #trigger>
                                <ActionButton
                                    size="small"
                                    type="secondary"
                                    square
                                    title="Modifier la dernière version minimale supportée de Magento 1"
                                    :icon="PencilSimpleIcon"
                                >
                                </ActionButton>
                            </template>
                        </SaveConfigurationValueAction>
                    </template>
                </MetricCard>
            </CardGrid>

            <CardGrid v-if="cms === 'magento2'" :columns="2">
                <MetricCard
                    title="Dernière version"
                    :icon="TagIcon"
                    :value="
                        globalData.latestMagento2Version.data.value ?? 'N/A'
                    "
                >
                    <template #actions>
                        <SaveConfigurationValueAction
                            configuration-key="latest_magento2_version"
                            title="Mettre à jour la dernière version de Magento 2"
                            label="Dernière version"
                            :callback="updateData"
                        >
                            <template #trigger>
                                <ActionButton
                                    size="small"
                                    type="secondary"
                                    square
                                    title="Modifier la dernière version de Magento 2"
                                    :icon="PencilSimpleIcon"
                                >
                                </ActionButton>
                            </template>
                        </SaveConfigurationValueAction>
                    </template>
                </MetricCard>

                <MetricCard
                    title="Dernière version minimale supportée"
                    :icon="ArrowLineDownIcon"
                    :value="
                        globalData.latestMagento2MinimumSupportedVersion.data
                            .value ?? 'N/A'
                    "
                >
                    <template #actions>
                        <SaveConfigurationValueAction
                            configuration-key="latest_magento2_minimum_supported_version"
                            title="Mettre à jour la dernière version minimale supportée de Magento 2"
                            label="Dernière version minimale supportée"
                            :callback="updateData"
                        >
                            <template #trigger>
                                <ActionButton
                                    size="small"
                                    type="secondary"
                                    square
                                    title="Modifier la dernière version minimale supportée de Magento 2"
                                    :icon="PencilSimpleIcon"
                                >
                                </ActionButton>
                            </template>
                        </SaveConfigurationValueAction>
                    </template>
                </MetricCard>
            </CardGrid>

            <CardGrid v-if="cms === 'prestashop'" :columns="2">
                <MetricCard
                    title="Dernière version"
                    :icon="TagIcon"
                    :value="
                        globalData.latestPrestashopVersion.data.value ?? 'N/A'
                    "
                >
                    <template #actions>
                        <SaveConfigurationValueAction
                            configuration-key="latest_prestashop_version"
                            title="Mettre à jour la dernière version de Prestashop"
                            label="Dernière version"
                            :callback="updateData"
                        >
                            <template #trigger>
                                <ActionButton
                                    size="small"
                                    type="secondary"
                                    square
                                    title="Modifier la dernière version de Prestashop"
                                    :icon="PencilSimpleIcon"
                                >
                                </ActionButton>
                            </template>
                        </SaveConfigurationValueAction>
                    </template>
                </MetricCard>

                <MetricCard
                    title="Dernière version minimale supportée"
                    :icon="ArrowLineDownIcon"
                    :value="
                        globalData.latestPrestashopMinimumSupportedVersion.data
                            .value ?? 'N/A'
                    "
                >
                    <template #actions>
                        <SaveConfigurationValueAction
                            configuration-key="latest_prestashop_minimum_supported_version"
                            title="Mettre à jour la dernière version minimale supportée de Prestashop"
                            label="Dernière version minimale supportée"
                            :callback="updateData"
                        >
                            <template #trigger>
                                <ActionButton
                                    size="small"
                                    type="secondary"
                                    square
                                    title="Modifier la dernière version minimale supportée de Prestashop"
                                    :icon="PencilSimpleIcon"
                                >
                                </ActionButton>
                            </template>
                        </SaveConfigurationValueAction>
                    </template>
                </MetricCard>
            </CardGrid>
        </template>
    </DataTableLayout>
</template>
